import { useEffect, useState } from 'react';
import { Plus, Trash2, Tag, Sliders, X, ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import { crmApi, Etiqueta, CampoCustom } from '../crmApi';

interface Props { session: any; }

const CORES = ['#3b82f6', '#22c55e', '#eab308', '#f97316', '#ef4444', '#a855f7', '#ec4899', '#14b8a6', '#94a3b8', '#64748b'];
const TIPOS: { value: CampoCustom['tipo']; label: string }[] = [
  { value: 'texto', label: 'Texto livre' },
  { value: 'numero', label: 'Número' },
  { value: 'select', label: 'Lista de opções' },
  { value: 'data', label: 'Data' },
  { value: 'checkbox', label: 'Checkbox (Sim/Não)' },
];
const ENTIDADES: { value: CampoCustom['entidade']; label: string }[] = [
  { value: 'lead', label: 'Leads' },
  { value: 'contato', label: 'Contatos' },
  { value: 'empresa-conta', label: 'Empresas' },
  { value: 'negocio', label: 'Oportunidades' },
];

const CRMConfiguracoes = ({ session }: Props) => {
  const [aba, setAba] = useState<'etiquetas' | 'campos'>('etiquetas');

  // ── Etiquetas ──
  const [etiquetas, setEtiquetas] = useState<Etiqueta[]>([]);
  const [carregandoEt, setCarregandoEt] = useState(false);
  const [novoNome, setNovoNome] = useState('');
  const [novaCor, setNovaCor] = useState(CORES[0]);
  const [salvandoEt, setSalvandoEt] = useState(false);

  const carregarEtiquetas = async () => {
    setCarregandoEt(true);
    try { setEtiquetas(await crmApi.listarEtiquetas(session)); }
    catch (e: any) { alert('Erro: ' + e.message); }
    finally { setCarregandoEt(false); }
  };
  useEffect(() => { carregarEtiquetas(); }, []); // eslint-disable-line

  const criarEtiqueta = async () => {
    if (!novoNome.trim()) { alert('Informe o nome da etiqueta'); return; }
    setSalvandoEt(true);
    try {
      await crmApi.criarEtiqueta(session, novoNome.trim(), novaCor);
      setNovoNome(''); carregarEtiquetas();
    } catch (e: any) { alert('Erro: ' + e.message); }
    finally { setSalvandoEt(false); }
  };

  const removerEtiqueta = async (e: Etiqueta) => {
    if (!confirm(`Remover etiqueta "${e.nome}"?`)) return;
    await crmApi.removerEtiqueta(session, e.id);
    carregarEtiquetas();
  };

  // ── Campos Custom ──
  const [campos, setCampos] = useState<CampoCustom[]>([]);
  const [carregandoCamp, setCarregandoCamp] = useState(false);
  const [filtroEntidade, setFiltroEntidade] = useState<CampoCustom['entidade']>('lead');
  const [showNovoCampo, setShowNovoCampo] = useState(false);
  const [novoCampo, setNovoCampo] = useState<Partial<CampoCustom>>({ tipo: 'texto', entidade: 'lead', obrigatorio: false });
  const [opcoesStr, setOpcoesStr] = useState(''); // opções separadas por vírgula
  const [salvandoCamp, setSalvandoCamp] = useState(false);

  const carregarCampos = async () => {
    setCarregandoCamp(true);
    try { setCampos(await crmApi.listarCamposCustom(session)); }
    catch (e: any) { alert('Erro: ' + e.message); }
    finally { setCarregandoCamp(false); }
  };

  useEffect(() => { if (aba === 'campos') carregarCampos(); }, [aba]); // eslint-disable-line

  const criarCampo = async () => {
    if (!novoCampo.nome?.trim()) { alert('Informe o nome do campo'); return; }
    setSalvandoCamp(true);
    try {
      const opcoes = novoCampo.tipo === 'select'
        ? opcoesStr.split(',').map(s => s.trim()).filter(Boolean)
        : [];
      await crmApi.criarCampoCustom(session, { ...novoCampo, opcoes });
      setNovoCampo({ tipo: 'texto', entidade: filtroEntidade, obrigatorio: false });
      setOpcoesStr('');
      setShowNovoCampo(false);
      carregarCampos();
    } catch (e: any) { alert('Erro: ' + e.message); }
    finally { setSalvandoCamp(false); }
  };

  const removerCampo = async (c: CampoCustom) => {
    if (!confirm(`Remover o campo "${c.nome}"?\nOs dados já salvos com esse campo serão mantidos no banco, mas não aparecerão mais nos formulários.`)) return;
    await crmApi.removerCampoCustom(session, c.id);
    carregarCampos();
  };

  const camposFiltrados = campos.filter(c => c.entidade === filtroEntidade);

  return (
    <div className="p-4 sm:p-6 max-w-3xl">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900">Configurações do CRM</h1>
        <p className="text-sm text-gray-600">Personalize etiquetas e campos dos formulários</p>
      </div>

      {/* Abas */}
      <div className="flex gap-1 mb-5 border-b border-gray-200">
        {([['etiquetas', <Tag className="h-4 w-4" />, 'Etiquetas'], ['campos', <Sliders className="h-4 w-4" />, 'Campos Personalizados']] as const).map(([id, icon, label]) => (
          <button key={id} onClick={() => setAba(id as any)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${aba === id ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {icon} {label}
          </button>
        ))}
      </div>

      {/* ── ABA ETIQUETAS ── */}
      {aba === 'etiquetas' && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Tag className="h-4 w-4" /> Etiquetas</h2>

          <div className="flex flex-wrap items-end gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex-1 min-w-[160px]">
              <label className="text-xs text-gray-600">Nome da etiqueta</label>
              <input value={novoNome} onChange={e => setNovoNome(e.target.value)} placeholder="Ex: VIP, Urgente, B2B..."
                onKeyDown={e => e.key === 'Enter' && criarEtiqueta()}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-600 block">Cor</label>
              <div className="flex gap-1 mt-1">
                {CORES.map(c => (
                  <button key={c} onClick={() => setNovaCor(c)}
                    className={`w-6 h-6 rounded-full border-2 ${novaCor === c ? 'border-gray-900' : 'border-transparent'}`}
                    style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>
            <button onClick={criarEtiqueta} disabled={salvandoEt}
              className="bg-black text-white px-3 py-2 rounded-lg flex items-center gap-1.5 hover:bg-gray-800 text-sm disabled:opacity-50">
              <Plus className="h-4 w-4" /> Adicionar
            </button>
          </div>

          {carregandoEt && <div className="text-sm text-gray-500">Carregando…</div>}
          {!carregandoEt && etiquetas.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-6">Nenhuma etiqueta cadastrada ainda.</p>
          )}
          <div className="flex flex-wrap gap-2">
            {etiquetas.map(e => (
              <div key={e.id} className="flex items-center gap-2 pl-3 pr-1 py-1 rounded-full border" style={{ backgroundColor: e.cor + '20', borderColor: e.cor + '60' }}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: e.cor }} />
                <span className="text-sm" style={{ color: e.cor }}>{e.nome}</span>
                <button onClick={() => removerEtiqueta(e)} className="p-1 rounded-full hover:bg-white/60">
                  <Trash2 className="h-3 w-3" style={{ color: e.cor }} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── ABA CAMPOS PERSONALIZADOS ── */}
      {aba === 'campos' && (
        <div className="space-y-4">
          {/* Filtro por entidade */}
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
            {ENTIDADES.map(e => (
              <button key={e.value} onClick={() => { setFiltroEntidade(e.value); setShowNovoCampo(false); setNovoCampo(p => ({ ...p, entidade: e.value })); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${filtroEntidade === e.value ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-800'}`}>
                {e.label}
              </button>
            ))}
          </div>

          {/* Lista de campos existentes */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900 text-sm">
                Campos de {ENTIDADES.find(e => e.value === filtroEntidade)?.label}
              </h2>
              <button onClick={() => setShowNovoCampo(s => !s)}
                className="flex items-center gap-1.5 bg-black text-white px-3 py-1.5 rounded-lg text-xs hover:bg-gray-800">
                <Plus className="h-3.5 w-3.5" /> Novo campo
              </button>
            </div>

            {/* Form novo campo */}
            {showNovoCampo && (
              <div className="border-b border-gray-100 p-4 bg-blue-50 space-y-3">
                <p className="text-xs font-semibold text-blue-700">Novo campo — {ENTIDADES.find(e => e.value === filtroEntidade)?.label}</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label className="text-xs text-gray-600">Nome do campo *</label>
                    <input value={novoCampo.nome || ''} onChange={e => setNovoCampo(p => ({ ...p, nome: e.target.value }))}
                      autoFocus placeholder="Ex: Responsável, Categoria, Prazo..."
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Tipo</label>
                    <select value={novoCampo.tipo} onChange={e => setNovoCampo(p => ({ ...p, tipo: e.target.value as any }))}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
                      {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                </div>

                {novoCampo.tipo === 'select' && (
                  <div>
                    <label className="text-xs text-gray-600">Opções (separadas por vírgula)</label>
                    <input value={opcoesStr} onChange={e => setOpcoesStr(e.target.value)}
                      placeholder="Ex: Pessoa Física, Pessoa Jurídica, Governo"
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                    {opcoesStr && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {opcoesStr.split(',').map(s => s.trim()).filter(Boolean).map(op => (
                          <span key={op} className="text-[11px] bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full">{op}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!novoCampo.obrigatorio} onChange={e => setNovoCampo(p => ({ ...p, obrigatorio: e.target.checked }))}
                      className="rounded border-gray-300 text-black h-4 w-4" />
                    <span className="text-xs text-gray-700">Campo obrigatório</span>
                  </label>
                  <div className="ml-auto flex gap-2">
                    <button onClick={() => { setShowNovoCampo(false); setNovoCampo({ tipo: 'texto', entidade: filtroEntidade, obrigatorio: false }); setOpcoesStr(''); }}
                      className="px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
                    <button onClick={criarCampo} disabled={salvandoCamp}
                      className="px-4 py-1.5 text-xs bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50">
                      {salvandoCamp ? 'Salvando…' : 'Criar campo'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {carregandoCamp && <div className="text-sm text-gray-500 p-4">Carregando…</div>}
            {!carregandoCamp && camposFiltrados.length === 0 && !showNovoCampo && (
              <div className="text-center py-10 text-gray-500">
                <Sliders className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">Nenhum campo personalizado para {ENTIDADES.find(e => e.value === filtroEntidade)?.label.toLowerCase()}.</p>
                <p className="text-xs text-gray-400 mt-1">Clique em "Novo campo" para criar.</p>
              </div>
            )}

            {camposFiltrados.map((c, idx) => (
              <div key={c.id} className={`flex items-center gap-3 px-4 py-3 hover:bg-gray-50 ${idx < camposFiltrados.length - 1 ? 'border-b border-gray-100' : ''}`}>
                <GripVertical className="h-4 w-4 text-gray-300 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {c.nome} {c.obrigatorio && <span className="text-red-500 text-xs">*</span>}
                  </p>
                  <p className="text-[11px] text-gray-500">
                    {TIPOS.find(t => t.value === c.tipo)?.label}
                    {c.tipo === 'select' && c.opcoes?.length ? ` · ${c.opcoes.join(', ')}` : ''}
                    {' · '}chave: <code className="text-[10px] bg-gray-100 px-1 rounded">{c.chave}</code>
                  </p>
                </div>
                <button onClick={() => removerCampo(c)} className="p-1.5 hover:bg-red-50 rounded text-red-500 flex-shrink-0">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>

          <div className="text-xs text-gray-400 bg-gray-50 rounded-lg p-3">
            <strong>Como funciona:</strong> Os campos criados aqui aparecem automaticamente nos formulários de{' '}
            {ENTIDADES.find(e => e.value === filtroEntidade)?.label.toLowerCase()}.
            A <strong>chave</strong> é o identificador interno (gerado automaticamente).
          </div>
        </div>
      )}
    </div>
  );
};

export default CRMConfiguracoes;
